import { useMemo, useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Link, useHistory } from 'react-router-dom'

import BannerContentWrapper from './BannerContentWrapper'

import theme from 'theme'
import useAgent from 'hooks/use-agent'
import Text from 'components/common/Text'
import useAuth from 'hooks/use-auth'
import useOnboardingData, {
  TECH_STACK_DISPLAY_FIELDS,
  TECH_STACK_DISPLAY_NAME_FIELDS,
  FIELD_CARDS_CONTENT,
  TECH_STACK_FIELDS_ICONS
} from 'hooks/use-onboarding-data'
import CardContainer from 'components/common/CardContainer'
import { SmallBadge } from 'components/common/Badge'
import Collapsible from 'components/common/Collapsible'
import TextButton from 'components/common/TextButton'
import PopoverMenu from 'components/common/PopoverMenu'
import { track } from 'track'
import { pluralize } from 'utils/strings'
import InfoTooltip from 'components/InfoTooltip'
import { hasBeenRun } from 'components/scenarios/utils'
import Icon from 'components/Icon'

const FieldIcon = ({ field, response = [] }) => {
  const currentCardContent = FIELD_CARDS_CONTENT[response?.[0]] ?? undefined

  const ResponseIcon = currentCardContent
    ? Array.isArray(currentCardContent)
      ? currentCardContent[0].icon
      : currentCardContent.icon
    : undefined

  if (response?.[0] && ResponseIcon) {
    return ResponseIcon()
  } else {
    const CurrentIcon = TECH_STACK_FIELDS_ICONS[field]
    return (
      <CurrentIcon
        css={{
          fontSize: 40
        }}
      />
    )
  }
}

export default function TechReadinessBanner({
  setIsNotificationOpen,
  ...rest
}) {
  const { currentTeamId } = useAuth()
  const { responses } = useOnboardingData()
  const [isOpen, setIsOpen] = useState(true)
  const history = useHistory()
  const { data: published, isLoading } = useAgent(
    `/scenarios?teamId=${currentTeamId}`,
    [],
    {
      shouldCache: true
    }
  )

  const hasNoResponses = useMemo(() => {
    return TECH_STACK_DISPLAY_FIELDS.every(
      field => responses?.[field]?.length === 0
    )
  }, [responses])

  const hasRecentlyRunScenarios = useMemo(() => {
    if (isLoading) return {}
    // get the date two weeks ago
    // get the object of categories with values the most recent date a scenario in that specific
    // category has ran
    // ie:
    //{
    //  messaging: <date>
    //  databases: <date>
    // }
    // if a category is absent it means no scenario has been ran in the last two weeks
    // so show the warning borders

    const hasRecentlyRunScenariosByCategories = published.reduce(
      (acc, scenario) => {
        if (hasBeenRun(scenario)) {
          const techCategories = scenario?.multi_select_tags?.techCategory ?? []
          if ((techCategories ?? []).length > 0) {
            techCategories.forEach(category => {
              if (!acc[category]) {
                acc[category] = scenario.last_run_at
              }
            })
          }
        }

        return acc
      },
      {}
    )

    return hasRecentlyRunScenariosByCategories
  }, [published, isLoading])

  const warningCategoriesCount =
    TECH_STACK_DISPLAY_FIELDS.length -
    Object.keys(hasRecentlyRunScenarios ?? {}).length

  const getAttackedStatusText = (status, category) => {
    const twoWeeksFromNow = moment().startOf('day').subtract(14, 'days')
    if (isLoading) return ''
    if (status[category]) {
      if (moment(status[category]).isAfter(twoWeeksFromNow)) {
        return `Last Attacked: ${moment(status[category]).format('MM/DD/YY')}`
      } else {
        return ''
      }
    } else {
      return 'Never attacked'
    }
  }

  return (
    <Collapsible
      collapseWithChevron
      headerStyle={{
        border: theme.border.base,
        paddingTop: theme.space[3],
        paddingLeft: theme.space[4],
        paddingRight: theme.space[4],
        paddingBottom: theme.space[3],
        borderTopLeftRadius: theme.borderRadius[0],
        borderTopRightRadius: theme.borderRadius[0],
        backgroundColor: theme.colors.gray[0]
      }}
      isOpen={isOpen}
      renderEllipsisMenu={({ getTargetProps, getPopoverProps }) => (
        <Fragment>
          {!isOpen && warningCategoriesCount > 0 && (
            <InfoTooltip
              css={{
                marginRight: theme.space[2],
                color: theme.colors.yellow[0],
                fontSize: theme.fontSizes[4]
              }}
              text={`${warningCategoriesCount} / ${
                TECH_STACK_DISPLAY_FIELDS.length
              } ${pluralize(
                'Need',
                warningCategoriesCount > 1 ? 1 : 2
              )} attention`}
              type="warning"
            />
          )}
          <Icon
            data-test-scenario-popover-actions
            icon="ellipsisVertical"
            {...getTargetProps({
              css: {
                fontSize: theme.fontSizes[3],
                color: theme.colors.steel[0],
                cursor: 'pointer',
                marginRight: theme.space[2],

                '&:hover': {
                  color: theme.colors.steel[2]
                }
              }
            })}
          />
          <PopoverMenu getPopoverProps={getPopoverProps}>
            <TextButton
              data-test-duplicate-button
              secondary
              data-track-action="edit-tech-stack"
              data-track-category="tech-readiness"
              data-track-on="click"
              onClick={event => {
                event.preventDefault()
                history.push(`/settings/team/${currentTeamId}/yourstack`)
              }}
            >
              Edit
            </TextButton>
          </PopoverMenu>
        </Fragment>
      )}
      setIsOpen={setIsOpen}
      title={
        <Text as="h4" color="medium">
          Technology Stack Readiness
        </Text>
      }
      onClick={() => {
        track('toggle_tech_stack_banner', {
          category: 'tech-readiness'
        })
      }}
    >
      <BannerContentWrapper
        canClose={false}
        css={{
          gridTemplateColumns: '1fr',
          maxHeight: 'none',
          paddingRight: theme.space[4],
          paddingLeft: theme.space[4],
          background: theme.colors.white[0],
          backgroundImage: 'none',
          borderRadius: theme.borderRadius[0],
          borderTop: 'none',
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0
        }}
        setIsNotificationOpen={setIsNotificationOpen}
        showContentTitle={false}
        subTitle={
          <Text as="p" css={{ color: theme.colors.steel[2] }}>
            Explore each component to test basic failure modes.
          </Text>
        }
        title={titleProps => {
          const { css, ...titleRest } = titleProps
          return (
            <Text as="h3" css={css} {...titleRest}>
              Get started by addressing common failure modes using Gremlin’s
              Recommended Scenarios.
            </Text>
          )
        }}
        {...rest}
      >
        {hasNoResponses && (
          <Text
            as={Link}
            css={{
              fontSize: theme.fontSizes[1],
              fontWeight: theme.fontWeights[0],
              textAlign: 'right',
              marginRight: theme.space[3]
            }}
            data-track-action="tech-readiness-add-tools"
            data-track-category="tech-readiness"
            data-track-on="click"
            to="/onboarding"
          >
            Add your tools for a custom experience
          </Text>
        )}
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, 180px)',
            gap: theme.space[3],
            marginTop: theme.space[3]
          }}
        >
          {(TECH_STACK_DISPLAY_FIELDS || []).map(field => {
            return (
              <CardContainer
                key={`tech-readiness-card-${field}`}
                css={{
                  gridTemplateRows: 'repeat(3, auto)',
                  minHeight: 180,
                  padding: theme.space[3],
                  paddingBottom: theme.space[2],
                  textAlign: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  borderColor:
                    hasRecentlyRunScenarios[field] || isLoading
                      ? theme.colors.border[0]
                      : theme.colors.yellow[0]
                }}
                data-track-action={`tech-readiness-${field}`}
                data-track-category="tech-readiness"
                data-track-on="click"
                disabled={isLoading}
                showHoverEffect={true}
                onClick={() => {
                  history.push({
                    pathname: `/scenarios/${
                      hasRecentlyRunScenarios[field]
                        ? 'my-scenarios'
                        : 'recommended'
                    }`,
                    state: {
                      filters: {
                        technology: { [field]: true }
                      },
                      from: 'dashboard'
                    }
                  })
                }}
              >
                <Text as="h4">{TECH_STACK_DISPLAY_NAME_FIELDS[field]}</Text>
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <FieldIcon field={field} response={responses[field]} />
                  {responses[field]?.length > 1 && (
                    <SmallBadge css={{ marginTop: theme.space[1] }}>
                      +{responses[field].length - 1}
                    </SmallBadge>
                  )}
                </div>
                <Text as="small">
                  {getAttackedStatusText(hasRecentlyRunScenarios, field)}
                </Text>
              </CardContainer>
            )
          })}
        </div>
      </BannerContentWrapper>
    </Collapsible>
  )
}

TechReadinessBanner.propTypes = {
  setIsNotificationOpen: PropTypes.func,
  canClose: PropTypes.bool
}
